import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Register listener FIRST (per Supabase auth guidance)
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      // Ao logar, hidrata os caches (obras/cadastros) do Supabase antes de liberar
      // as telas protegidas — os getters síncronos passam a refletir o banco.
      if (event === "SIGNED_IN" && newSession?.user) {
        setLoading(true);
        import("@/services/hydrateAll")
          .then((m) => m.hydrateAll())
          .catch((e) => console.error("hydrateAll:", e))
          .finally(() => setLoading(false));
      }
    });

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        try {
          const { hydrateAll } = await import("@/services/hydrateAll");
          await hydrateAll();
        } catch (e) {
          console.error("hydrateAll:", e);
        }
      }
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("supabase.auth.") || k.includes("sb-")) localStorage.removeItem(k);
      });
    } catch {}
    await supabase.auth.signOut({ scope: "global" }).catch(() => {});
    window.location.href = "/login";
  }, []);

  // Memoizado: sem isto, cada render do provider entrega um objeto novo e
  // re-renderiza todos os consumidores de useAuth desnecessariamente.
  const value = useMemo(
    () => ({ user, session, loading, signOut }),
    [user, session, loading, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
